/**
 * Shared bbox normalization utilities.
 * All vendors must produce CanonicalBbox in 0-1 range.
 */

import { Data, Effect, Schema, SchemaIssue } from 'effect';
import type { CanonicalBbox } from './types';

const RawPixelBboxSchema = Schema.Struct({
  x: Schema.Finite,
  y: Schema.Finite,
  w: Schema.Finite,
  h: Schema.Finite,
});

const PixelBboxInputSchema = Schema.Struct({
  raw: RawPixelBboxSchema,
  pageW: Schema.NullOr(Schema.UndefinedOr(Schema.Finite)),
  pageH: Schema.NullOr(Schema.UndefinedOr(Schema.Finite)),
});

const PolygonBboxInputSchema = Schema.Struct({
  polygon: Schema.UndefinedOr(Schema.mutable(Schema.Array(Schema.Finite))),
  pageW: Schema.Finite,
  pageH: Schema.Finite,
});

const Bbox2dInputSchema = Schema.mutable(Schema.Array(Schema.Finite)).check(
  Schema.isLengthBetween(4, 4),
);

export type BboxNormalizationOperation = 'pixel' | 'polygon' | 'bbox-2d';
export type BboxNormalizationIssue = {
  readonly path: ReadonlyArray<PropertyKey>;
};

/** Redacted vendor-coordinate failure that never retains raw geometry payloads. */
export class BboxNormalizationError extends Data.TaggedError('BboxNormalizationError')<{
  readonly operation: BboxNormalizationOperation;
  readonly issues: ReadonlyArray<BboxNormalizationIssue>;
}> {}

const formatStandardSchemaIssue = SchemaIssue.makeFormatterStandardSchemaV1();

function bboxNormalizationError(
  operation: BboxNormalizationOperation,
  cause: unknown,
): BboxNormalizationError {
  const issues: ReadonlyArray<BboxNormalizationIssue> = Schema.isSchemaError(cause)
    ? formatStandardSchemaIssue(cause.issue).issues.map((issue) => ({
        path: issue.path?.map((segment) =>
          typeof segment === 'object' ? segment.key : segment,
        ) ?? [],
      }))
    : [{ path: [] }];
  return new BboxNormalizationError({ operation, issues });
}

function decodeBboxInputEffect<S extends Schema.ConstraintDecoder<unknown>>(
  operation: BboxNormalizationOperation,
  schema: S,
  input: unknown,
): Effect.Effect<S['Type'], BboxNormalizationError> {
  return Schema.decodeUnknownEffect(schema, { errors: 'all' })(input).pipe(
    Effect.mapError((cause) => bboxNormalizationError(operation, cause)),
  );
}

/** Pixel bbox → 0-1. Returns undefined if dims missing/zero. */
function normalizePixelBboxUnsafe(
  raw: { x: number; y: number; w: number; h: number },
  pageW: number | undefined | null,
  pageH: number | undefined | null,
): CanonicalBbox | undefined {
  if (!pageW || !pageH || pageW <= 0 || pageH <= 0) return undefined;
  return { x: raw.x / pageW, y: raw.y / pageH, w: raw.w / pageW, h: raw.h / pageH };
}

/** Polygon points + page dims → 0-1. (Azure DI inch-space polygons) */
function normalizePolygonBboxUnsafe(
  polygon: number[] | undefined,
  pageW: number,
  pageH: number,
): CanonicalBbox | undefined {
  if (!polygon || polygon.length < 4 || !pageW || !pageH) return undefined;
  const xs = polygon.filter((_, i) => i % 2 === 0);
  const ys = polygon.filter((_, i) => i % 2 === 1);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return {
    x: minX / pageW,
    y: minY / pageH,
    w: (Math.max(...xs) - minX) / pageW,
    h: (Math.max(...ys) - minY) / pageH,
  };
}

/** 0-1000 [x1,y1,x2,y2] → 0-1 CanonicalBbox */
function normalizeBbox2dUnsafe(bbox2d: number[]): CanonicalBbox {
  const [x1, y1, x2, y2] = bbox2d;
  return { x: x1 / 1000, y: y1 / 1000, w: (x2 - x1) / 1000, h: (y2 - y1) / 1000 };
}

/** Native parser boundary for pixel-space vendor boxes. */
export const normalizePixelBboxEffect = Effect.fn('ParserSdkBbox.normalizePixel')(
  function* (raw: unknown, pageW: unknown, pageH: unknown) {
    const input = yield* decodeBboxInputEffect('pixel', PixelBboxInputSchema, {
      raw,
      pageW,
      pageH,
    });
    return normalizePixelBboxUnsafe(input.raw, input.pageW, input.pageH);
  },
);

/** Existing synchronous API derived from the validated native program. */
export function normalizePixelBbox(
  raw: { x: number; y: number; w: number; h: number },
  pageW: number | undefined | null,
  pageH: number | undefined | null,
): CanonicalBbox | undefined {
  return Effect.runSync(normalizePixelBboxEffect(raw, pageW, pageH));
}

/** Native parser boundary for polygon-space vendor boxes. */
export const normalizePolygonBboxEffect = Effect.fn('ParserSdkBbox.normalizePolygon')(
  function* (polygon: unknown, pageW: unknown, pageH: unknown) {
    const input = yield* decodeBboxInputEffect('polygon', PolygonBboxInputSchema, {
      polygon,
      pageW,
      pageH,
    });
    return normalizePolygonBboxUnsafe(input.polygon, input.pageW, input.pageH);
  },
);

/** Existing synchronous API derived from the validated native program. */
export function normalizePolygonBbox(
  polygon: number[] | undefined,
  pageW: number,
  pageH: number,
): CanonicalBbox | undefined {
  return Effect.runSync(normalizePolygonBboxEffect(polygon, pageW, pageH));
}

/** Native parser boundary for 0-1000 model coordinates. */
export const normalizeBbox2dEffect = Effect.fn('ParserSdkBbox.normalizeBbox2d')(
  function* (bbox2d: unknown) {
    const input = yield* decodeBboxInputEffect('bbox-2d', Bbox2dInputSchema, bbox2d);
    return normalizeBbox2dUnsafe(input);
  },
);

/** Existing synchronous API derived from the validated native program. */
export function normalizeBbox2d(bbox2d: number[]): CanonicalBbox {
  return Effect.runSync(normalizeBbox2dEffect(bbox2d));
}

/** Internal {w,h} → API-facing {width,height} */
export function bboxToApi(b: CanonicalBbox): { x: number; y: number; width: number; height: number } {
  return { x: b.x, y: b.y, width: b.w, height: b.h };
}

/** Normalized-bbox intersection test (0-1 space). */
export function bboxesIntersect(a: CanonicalBbox, b: CanonicalBbox): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/**
 * Clip a page-space bbox into region space. Returns undefined when the bbox
 * does not intersect the region. Output is 0-1 relative to the region.
 */
export function bboxToRegionSpace(
  bbox: CanonicalBbox,
  region: CanonicalBbox,
): CanonicalBbox | undefined {
  if (!bboxesIntersect(bbox, region)) return undefined;
  if (region.w <= 0 || region.h <= 0) return undefined;
  const left = Math.max(bbox.x, region.x);
  const top = Math.max(bbox.y, region.y);
  const right = Math.min(bbox.x + bbox.w, region.x + region.w);
  const bottom = Math.min(bbox.y + bbox.h, region.y + region.h);
  const clamp01 = (value: number) => Math.min(1, Math.max(0, value));
  return {
    x: clamp01((left - region.x) / region.w),
    y: clamp01((top - region.y) / region.h),
    w: clamp01((right - left) / region.w),
    h: clamp01((bottom - top) / region.h),
  };
}

/** Re-project a region-space bbox back into page space (0-1). */
export function bboxFromRegionSpace(bbox: CanonicalBbox, region: CanonicalBbox): CanonicalBbox {
  const clamp01 = (value: number) => Math.min(1, Math.max(0, value));
  return {
    x: clamp01(region.x + bbox.x * region.w),
    y: clamp01(region.y + bbox.y * region.h),
    w: clamp01(bbox.w * region.w),
    h: clamp01(bbox.h * region.h),
  };
}
